import ArrowLeft from '@assets/icons/arrow-left.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useState } from 'react';
import { AccountForm } from '@/components/locations/account-form';
import { AddressInputType } from '@domain/addresses/types/types';
import { CompositeScreenProps } from '@react-navigation/native';
import {
  CreationSourceFeatures,
  Locations_CreateAccountMutation,
  Locations_CreateAccountMutationVariables,
} from '@/types';
import {
  EMPLOYEE_MANAGEMENT_ROUTES,
  LOCATION_MANAGER_ROUTES,
} from '@/navigators/routes';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation } from '@apollo/client';
import {
  LocationManagerStackParams,
  RestaurantStackParams,
} from '@/navigators/types';
import { ModalContainer } from '@/components/modal-container';
import { StackScreenProps } from '@react-navigation/stack';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { USER_QUERY } from '@/contexts/user-context-queries';
import { useToasts } from '@/contexts/toast-context';

export const CREATE_RESTAURANT_MUTATION = gql`
  mutation Locations_CreateAccount(
    $supplierUUID: uuid!
    $name: String!
    $address: String!
    $address2: String
    $accountID: String
    $notes: String
    $routeStop: String
    $metadata: RestaurantMetadataInput
    $setAsProspect: Boolean
    $creationSourceFeature: CreationSourceFeatures
  ) {
    supplierCreateRestaurant(
      supplier_id: $supplierUUID
      restaurant_name: $name
      restaurant_address: $address
      restaurant_address2: $address2
      restaurant_account_id: $accountID
      restaurant_notes: $notes
      route_stop: $routeStop
      restaurant_metadata: $metadata
      set_as_prospect: $setAsProspect
      creation_source_feature: $creationSourceFeature
    ) {
      business {
        name
        uuid
      }
    }
  }
`;

type Props = CompositeScreenProps<
  StackScreenProps<
    LocationManagerStackParams,
    LOCATION_MANAGER_ROUTES.CreateAccount
  >,
  StackScreenProps<RestaurantStackParams, EMPLOYEE_MANAGEMENT_ROUTES.Employees>
>;

export const CreateAccountScreen: React.FC<Props> = ({ navigation, route }) => {
  const { initialName, onClose } = route.params;
  const { isSmallLayout } = useLayoutContext();
  const { selectRestaurant, selectedRestaurantRelationship } =
    useAuthenticatedUserContext();
  const { supplierUUID } = selectedRestaurantRelationship;
  const { showErrorToast } = useToasts();

  const [name, setName] = useState(initialName || '');
  const [address, setAddress] = useState<AddressInputType | null>(null);
  const [accountID, setAccountID] = useState('');
  const [address2, setAddress2] = useState('');
  const [notes, setNotes] = useState('');
  const [routeStop, setRouteStop] = useState('');
  const [isProspect, setIsProspect] = useState(false);

  useEffect(() => {
    // Listen to beforeRemove since the user can close modals via the overlay
    return navigation.addListener('beforeRemove', () => {
      onClose?.();
    });
  }, [navigation, onClose]);

  const addressInputAddress2 = address?.address2;
  useEffect(() => {
    if (addressInputAddress2) {
      setAddress2(addressInputAddress2);
    }
  }, [addressInputAddress2]);

  const [createRestaurant, { loading }] = useMutation<
    Locations_CreateAccountMutation,
    Locations_CreateAccountMutationVariables
  >(CREATE_RESTAURANT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [getOperationName(USER_QUERY) as string],
  });

  return (
    <ModalContainer
      navigationHeaderProps={{
        BackButtonIcon: ArrowLeft,
        showBackButton: isSmallLayout,
        showShadow: true,
        title: i18n.t('create_account'),
      }}
    >
      <AccountForm
        accountID={accountID}
        address={address}
        address2={address2}
        buttonProps={{
          disabled: !name || !address,
          loading,
          onPress: async () => {
            try {
              const { data: createRestaurantData } = await createRestaurant({
                variables: {
                  accountID: accountID || null,
                  address: address?.address!,
                  address2: address2 || null,
                  creationSourceFeature: CreationSourceFeatures.Custom,
                  name,
                  notes: notes || null,
                  routeStop: routeStop || null,
                  setAsProspect: isProspect,
                  supplierUUID,
                },
              });

              const business =
                createRestaurantData?.supplierCreateRestaurant.business;
              const restaurantName = business?.name!;
              const newAccountUUID = business?.uuid!;

              selectRestaurant(newAccountUUID);

              navigation.push(LOCATION_MANAGER_ROUTES.InviteEmployee, {
                onComplete: () => {
                  navigation.navigate(EMPLOYEE_MANAGEMENT_ROUTES.Employees);
                },
                restaurantName,
                restaurantUUID: newAccountUUID,
              });
            } catch (e) {
              showErrorToast();
            }
          },
          trackingData: {
            name: 'create_account.submit_button',
          },
        }}
        buttonText={i18n.t('save')}
        isProspect={isProspect}
        name={name}
        notes={notes}
        onAccountIDChange={setAccountID}
        onAddress2Change={setAddress2}
        onAddressChange={setAddress}
        onNameChange={setName}
        onNotesChange={setNotes}
        onRouteStopChange={setRouteStop}
        routeStop={routeStop}
        setIsProspect={setIsProspect}
      />
    </ModalContainer>
  );
};
